import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Download, Ellipsis, Minus, Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Indicador, Segmentado, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { aceitoDaUrl, aceitosDaUrl, ChipsFixos, contarFiltros, FiltroParceiros, FiltroVarios, LinhaChips, MaisFiltros, opcoesComEscolhido, PeriodoComBase, periodoDaUrl, uuidDaUrl, uuidsDaUrl } from '../../componentes/filtros-lista';
import { filtrosNaUrl } from '../../componentes/filtros-visao';
import { Menu, type ItemMenu } from '../../componentes/menu';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { get } from '../../nucleo/api';
import { data, dinheiro, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { caminhoDaCategoria, useCategorias, useCentros, useContas } from './dados';
import { DetalheLancamento } from './DetalheLancamento';
import { ExportarContabil } from './ExportarContabil';
import { FormLancamento } from './FormLancamento';
import { FormTransferencia } from './FormTransferencia';
import { corDoValor, type Lancamento, type Totais } from './tipos';
import { descricaoGerada } from '../../nucleo/texto-sistema';
import { anunciarOcultos } from '../../componentes/sensivel';
import { rotuloCategoria } from '../../nucleo/rotulos';

/** No celular, as ações secundárias do cabeçalho viram um menu simples para o primário ficar à vista. */
function MaisAcoes({ itens }: { itens: ItemMenu[] }) {
  const { t } = useTranslation('financeiro');
  // Menu do núcleo (r6 visual B7): setas, Home, End e Esc com o foco nos itens.
  return <Menu className="sm:hidden" rotulo={t('acoes.maisAcoes')} icone={<Ellipsis className="size-4" />} itens={itens} />;
}

const TIPOS = ['entrada', 'saida', 'transferencia'] as const;
const ORIGENS = ['manual', 'titulo', 'modulo'] as const;
type TipoMov = (typeof TIPOS)[number];

/**
 * C2: o alternador "Todas | Entradas | Saídas" e os cards escrevem o mesmo `tipo` da URL. O alternador mostra "Entradas" ou
 * "Saídas" só com esse tipo sozinho; o resto (nenhum, transferência, vários de uma visão antiga) é "Todas".
 */
export function direcaoDoTipo(tipos: readonly string[]): 'todas' | 'entrada' | 'saida' {
  return tipos.length === 1 && (tipos[0] === 'entrada' || tipos[0] === 'saida') ? tipos[0] : 'todas';
}

/** C2: card ativo pelo tipo da URL: o tipo sozinho, '' (Resultado) sem tipo e nenhum com vários tipos. */
export function cardDoTipo(tipos: readonly string[]): TipoMov | '' | null {
  if (!tipos.length) return '';
  return tipos.length === 1 ? (tipos[0] as TipoMov) : null;
}

/** C2: clicar no card ativo volta ao Resultado (sem tipo); clicar em outro filtra por ele. */
export function tipoDoCard(tipos: readonly string[], clicado: TipoMov | ''): string {
  return clicado && cardDoTipo(tipos) !== clicado ? clicado : '';
}

/** M4.3 e M4.7: entradas e saídas com filtros, totais, novo lançamento com comprovante e estorno. */
export function EntradasSaidas() {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [busca, setBusca] = useSearchParams();
  const [novo, setNovo] = useState<'entrada' | 'saida' | null>(null);
  const [transferir, setTransferir] = useState(false);
  const [exportar, setExportar] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const contas = useContas();
  const categorias = useCategorias();
  const centros = useCentros();

  // Filtros ficam na URL: link da ficha do parceiro e da tela de contas chega já filtrado.
  const filtro = (k: string) => busca.get(k) ?? '';
  const mudarVarios = (parcial: Record<string, string>) => setBusca((atual) => filtrosNaUrl(atual, parcial), { replace: true });
  const mudar = (k: string, v: string) => mudarVarios({ [k]: v });
  // Fase 2 (W36): período por caixa (padrão) ou competência no `SeletorPeriodo`, origem e clientes ou fornecedores com chips.
  // Review r28 spec (B1): o par de datas só vale junto (o mesmo no `FiltroPeriodo` e na API).
  const periodo = periodoDaUrl(filtro('de'), filtro('ate'));
  const params = useMemo(
    () => ({
      campo_data: filtro('campo_data') === 'competencia' ? 'competencia' : undefined,
      // Review r27 spec (B1): data inválida na URL (`?de=xx`) também é ignorada (antes, 422 e a lista "Indisponível").
      de: periodo.de || undefined,
      ate: periodo.ate || undefined,
      // Review r25 spec (B1): contas e tipos em lista (vírgula), como as movimentações do antigo.
      // Review r26 robustez (O6): valor que a API recusaria (`?tipo=xx`) é ignorado; antes, 422 e a tela "Indisponível".
      conta_id: uuidsDaUrl(filtro('conta_id')).join(',') || undefined,
      categoria_id: uuidDaUrl(filtro('categoria_id')) || undefined,
      centro_custo_id: uuidDaUrl(filtro('centro_custo_id')) || undefined,
      tipo: aceitosDaUrl(filtro('tipo'), TIPOS).join(',') || undefined,
      conciliado: aceitoDaUrl(filtro('conciliado'), ['true', 'false']) || undefined,
      // Review r26 spec (O3): origens em lista (vírgula), OU entre elas, como os tipos do antigo; uma só segue valendo.
      origem: aceitosDaUrl(filtro('origem'), ORIGENS).join(',') || undefined,
      parceiro_id: uuidsDaUrl(filtro('parceiro_id')).join(',') || undefined,
    }),
    [busca], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filtrando = Object.values(params).some(Boolean);
  // C2: os cards são recortes por tipo, então os totais seguem todos os filtros menos o tipo (antes, filtrar por entrada
  // zerava o card Saídas).
  const paramsTotais = useMemo(() => ({ ...params, tipo: undefined }), [params]);
  const filtrandoTotais = Object.values(paramsTotais).some(Boolean);
  const totais = useQuery({ queryKey: ['/financeiro/lancamentos', 'totais', paramsTotais], queryFn: () => get<Totais>('/financeiro/lancamentos/totais', paramsTotais) });
  const tiposUrl = aceitosDaUrl(filtro('tipo'), TIPOS);
  const cardAtivo = cardDoTipo(tiposUrl);
  const direcao = direcaoDoTipo(tiposUrl);
  const contasUrl = uuidsDaUrl(filtro('conta_id'));
  // C2: a coluna "Saldo acumulado" do antigo, só com uma conta filtrada (com várias, o saldo não é de uma conta).
  const comSaldo = contasUrl.length === 1;
  const moeda = s.workspace.moeda;
  const todasCategorias = categorias.data?.itens ?? [];
  // Review r27 spec (B2): categoria ou centro da URL fora das opções vira a opção "Cadastro sem acesso" (antes, "Todas" com a
  // lista filtrada e 0 linhas). Enquanto as opções carregam, "...".
  const categoriaUrl = uuidDaUrl(filtro('categoria_id'));
  const centroUrl = uuidDaUrl(filtro('centro_custo_id'));
  const opcoesCategorias = opcoesComEscolhido(
    todasCategorias.map((c) => ({ valor: c.id, rotulo: caminhoDaCategoria(c, todasCategorias) })),
    categoriaUrl,
    categorias.isPending ? '...' : tn('filtrosLista.semAcessoCampo', { campo: t('campos.categoria') }),
  );
  const opcoesCentros = opcoesComEscolhido(
    (centros.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome })),
    centroUrl,
    centros.isPending ? '...' : tn('filtrosLista.semAcessoCampo', { campo: t('campos.centroCusto') }),
  );

  const colunas: Coluna<Lancamento>[] = [
    { chave: 'data_caixa', rotulo: t('campos.dataCaixa'), ordenavel: true, mono: true, render: (l) => data(l.data_caixa) },
    {
      chave: 'descricao',
      rotulo: t('campos.descricao'),
      principal: true,
      render: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium" title={descricaoGerada(l.descricao)}>{descricaoGerada(l.descricao)}</span>
          {l.parceiro_nome && <span className="truncate text-xs text-tinta-3" title={nomePessoa(l.parceiro_nome)}>{nomePessoa(l.parceiro_nome)}</span>}
        </span>
      ),
    },
    {
      chave: 'tipo',
      rotulo: t('campos.tipo'),
      render: (l) => (
        <span className="flex flex-wrap gap-1">
          <Selo cor={l.tipo === 'entrada' ? 'verde' : l.tipo === 'saida' ? 'perigo' : 'azul'}>{t(`tipo.${l.tipo}`)}</Selo>
          {l.estorno_de && <Selo cor="laranja">{t('estado.estorno')}</Selo>}
          {l.estornado_por && <Selo cor="laranja">{t('estado.estornado')}</Selo>}
        </span>
      ),
    },
    { chave: 'conta_nome', rotulo: t('campos.conta'), render: (l) => (l.tipo === 'transferencia' ? `${l.conta_nome} > ${l.conta_destino_nome}` : l.conta_nome) },
    { chave: 'categoria_nome', rotulo: t('campos.categoria'), render: (l) => rotuloCategoria(l.categoria_nome) },
    { chave: 'centro_custo_nome', rotulo: t('campos.centroCusto'), oculta: true, render: (l) => l.centro_custo_nome ?? '' },
    { chave: 'data_competencia', rotulo: t('campos.dataCompetencia'), ordenavel: true, oculta: true, mono: true, render: (l) => data(l.data_competencia) },
    {
      chave: 'valor_centavos',
      rotulo: t('campos.valor'),
      ordenavel: true,
      alinhar: 'direita',
      mono: true,
      render: (l) => (
        <span className={corDoValor(l.tipo)}>
          {l.tipo === 'entrada' ? '+' : l.tipo === 'saida' ? '-' : ''}
          {anunciarOcultos(dinheiro(l.valor_centavos, l.moeda))}
        </span>
      ),
    },
    ...(comSaldo
      ? [
          {
            chave: 'saldo_acumulado_centavos',
            rotulo: t('campos.saldoAcumulado'),
            alinhar: 'direita',
            mono: true,
            render: (l: Lancamento) => (l.saldo_acumulado_centavos === undefined ? '' : <span className={l.saldo_acumulado_centavos < 0 ? 'text-perigo' : undefined}>{anunciarOcultos(dinheiro(l.saldo_acumulado_centavos, l.moeda))}</span>),
          } satisfies Coluna<Lancamento>,
        ]
      : []),
    { chave: 'conciliado', rotulo: t('campos.conciliado'), render: (l) => (l.conciliado ? <Selo cor="verde">{t('estado.conciliado')}</Selo> : <span className="text-tinta-3">{t('estado.pendente')}</span>) },
  ];
  const opcoesConciliado = [
    { valor: 'true', rotulo: t('estado.conciliado') },
    { valor: 'false', rotulo: t('estado.pendente') },
  ];
  const conciliadoUrl = aceitoDaUrl(filtro('conciliado'), ['true', 'false']);
  const rotuloDe = (opcoes: { valor: string; rotulo: string }[], v: string) => opcoes.find((o) => o.valor === v)?.rotulo ?? v;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <AcaoPrimaria>
        <MaisAcoes
          itens={[
            ...(s.pode('financeiro.exportar') ? [{ chave: 'exportar', rotulo: t('acoes.exportar'), icone: <Download className="size-4" />, aoClicar: () => setExportar(true) }] : []),
            ...(s.pode('financeiro.criar')
              ? [
                  { chave: 'transferir', rotulo: t('acoes.transferir'), icone: <ArrowLeftRight className="size-4" />, aoClicar: () => setTransferir(true) },
                  { chave: 'saida', rotulo: t('acoes.novaSaida'), icone: <Minus className="size-4" />, aoClicar: () => setNovo('saida') },
                ]
              : []),
          ]}
        />
        <div className="hidden items-center gap-2 sm:flex">
          {s.pode('financeiro.exportar') && (
            <Botao icone={<Download className="size-4" />} onClick={() => setExportar(true)}>
              {t('acoes.exportar')}
            </Botao>
          )}
          {s.pode('financeiro.criar') && (
            <>
              <Botao icone={<ArrowLeftRight className="size-4" />} onClick={() => setTransferir(true)}>
                {t('acoes.transferir')}
              </Botao>
              <Botao icone={<Minus className="size-4" />} onClick={() => setNovo('saida')}>
                {t('acoes.novaSaida')}
              </Botao>
            </>
          )}
        </div>
        {s.pode('financeiro.criar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo('entrada')}>
            {t('acoes.novaEntrada')}
          </Botao>
        )}
      </AcaoPrimaria>

      {/* C2: os 3 cards de antes, agora clicáveis (recortam pelo tipo), mais o card Transferências; o Resultado é o "Total". */}
      <section aria-label={t('totais.titulo')} className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-cards-movimentacoes="">
        <Indicador rotulo={t('totais.entradas')} cor="verde" valor={totais.data ? dinheiro(totais.data.entradas_centavos, moeda) : '...'} detalhe={totais.data ? t('totais.lancamentos', { count: totais.data.quantidade }) : undefined} ativo={cardAtivo === 'entrada'} aoClicar={() => mudar('tipo', tipoDoCard(tiposUrl, 'entrada'))} data-card="entrada" />
        <Indicador rotulo={t('totais.saidas')} cor="perigo" valor={totais.data ? dinheiro(totais.data.saidas_centavos, moeda) : '...'} detalhe={totais.data && totais.data.transferencias_centavos ? t('totais.transferencias', { valor: dinheiro(totais.data.transferencias_centavos, moeda) }) : undefined} ativo={cardAtivo === 'saida'} aoClicar={() => mudar('tipo', tipoDoCard(tiposUrl, 'saida'))} data-card="saida" />
        <Indicador rotulo={t('totais.transferenciasTitulo')} cor="ciano" valor={totais.data ? dinheiro(totais.data.transferencias_centavos, moeda) : '...'} detalhe={totais.data ? t('totais.transferenciasQtd', { count: totais.data.transferencias }) : undefined} ativo={cardAtivo === 'transferencia'} aoClicar={() => mudar('tipo', tipoDoCard(tiposUrl, 'transferencia'))} data-card="transferencia" />
        <Indicador rotulo={t('totais.saldo')} cor={totais.data && totais.data.saldo_centavos < 0 ? 'perigo' : 'tinta-1'} valor={totais.data ? dinheiro(totais.data.saldo_centavos, moeda) : '...'} detalhe={filtrandoTotais ? t('totais.comFiltros') : t('totais.todoPeriodo')} ativo={cardAtivo === ''} aoClicar={() => mudar('tipo', '')} data-card="resultado" />
      </section>

      {/* C2: a faixa do antigo (período, Todas | Entradas | Saídas e "Mais filtros" com contas e tipos de movimentação),
          com os filtros do MalgoOS no painel e os escolhidos na linha de chips, como em A receber e A pagar (C1). */}
      <section aria-label={tn('comum.filtrar')} className="flex min-w-0 flex-col gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <PeriodoComBase bases={['caixa', 'competencia']} campo={filtro('campo_data')} de={periodo.de} ate={periodo.ate} aoMudar={mudarVarios} />
          <Segmentado
            rotulo={t('filtros.direcao')}
            valor={direcao}
            opcoes={[
              { valor: 'todas', rotulo: t('filtros.todas') },
              { valor: 'entrada', rotulo: t('filtros.soEntradas') },
              { valor: 'saida', rotulo: t('filtros.soSaidas') },
            ]}
            aoMudar={(v) => mudar('tipo', v === 'todas' ? '' : v)}
            data-filtro-direcao=""
          />
          <MaisFiltros contagem={contarFiltros([params.conta_id, params.origem, params.categoria_id, params.centro_custo_id, params.conciliado, params.parceiro_id])}>
            <FiltroVarios rotulo={t('campos.conta')} todos={t('filtros.todasContas')} opcoes={(contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))} ids={contasUrl} aoMudar={(ids) => mudar('conta_id', ids.join(','))} parte="campo" data-filtro-contas="" />
            <FiltroVarios
              rotulo={t('filtros.origem')}
              todos={t('filtros.qualquerOrigem')}
              opcoes={ORIGENS.map((x) => ({ valor: x, rotulo: t(`filtros.origens.${x}`) }))}
              ids={aceitosDaUrl(filtro('origem'), ORIGENS)}
              aoMudar={(ids) => mudar('origem', ids.join(','))}
              parte="campo"
              data-filtro-origens=""
            />
            <div className="col-span-2 sm:col-span-1">
              <Selecao aria-label={t('campos.categoria')} vazio={t('filtros.todasCategorias')} opcoes={opcoesCategorias} value={categoriaUrl} onChange={(e) => mudar('categoria_id', e.target.value)} data-filtro-categoria="" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Selecao aria-label={t('campos.centroCusto')} vazio={t('filtros.todosCentros')} opcoes={opcoesCentros} value={centroUrl} onChange={(e) => mudar('centro_custo_id', e.target.value)} data-filtro-centro="" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Selecao aria-label={t('campos.conciliado')} vazio={t('filtros.qualquerConciliacao')} opcoes={opcoesConciliado} value={conciliadoUrl} onChange={(e) => mudar('conciliado', e.target.value)} />
            </div>
            <FiltroParceiros parte="campo" papel="todos" ids={uuidsDaUrl(filtro('parceiro_id'))} aoMudar={(ids) => mudar('parceiro_id', ids.join(','))} />
          </MaisFiltros>
        </div>
        <LinhaChips visivel={filtrando} rotuloLimpar={t('filtros.limpar')} aoLimpar={() => setBusca({}, { replace: true })}>
          <FiltroVarios rotulo={t('campos.conta')} todos={t('filtros.todasContas')} opcoes={(contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))} ids={contasUrl} aoMudar={(ids) => mudar('conta_id', ids.join(','))} parte="chips" />
          {/* Transferência ou vários tipos (visão salva antes da C2) não cabem no alternador: ficam à vista como chips. */}
          {direcao === 'todas' && (
            <FiltroVarios rotulo={t('campos.tipo')} todos={t('filtros.todosTipos')} opcoes={TIPOS.map((x) => ({ valor: x, rotulo: t(`tipo.${x}`) }))} ids={tiposUrl} aoMudar={(ids) => mudar('tipo', ids.join(','))} parte="chips" data-filtro-tipos="" />
          )}
          <FiltroVarios rotulo={t('filtros.origem')} todos={t('filtros.qualquerOrigem')} opcoes={ORIGENS.map((x) => ({ valor: x, rotulo: t(`filtros.origens.${x}`) }))} ids={aceitosDaUrl(filtro('origem'), ORIGENS)} aoMudar={(ids) => mudar('origem', ids.join(','))} parte="chips" />
          <ChipsFixos itens={categoriaUrl ? [{ chave: categoriaUrl, rotulo: rotuloDe(opcoesCategorias, categoriaUrl) }] : []} rotulo={t('campos.categoria')} aoTirar={() => mudar('categoria_id', '')} data-chips-categoria="" />
          <ChipsFixos itens={centroUrl ? [{ chave: centroUrl, rotulo: rotuloDe(opcoesCentros, centroUrl) }] : []} rotulo={t('campos.centroCusto')} aoTirar={() => mudar('centro_custo_id', '')} data-chips-centro="" />
          <ChipsFixos itens={conciliadoUrl ? [{ chave: conciliadoUrl, rotulo: `${t('campos.conciliado')}: ${rotuloDe(opcoesConciliado, conciliadoUrl)}` }] : []} rotulo={t('campos.conciliado')} aoTirar={() => mudar('conciliado', '')} data-chips-conciliado="" />
          <FiltroParceiros parte="chips" papel="todos" ids={uuidsDaUrl(filtro('parceiro_id'))} aoMudar={(ids) => mudar('parceiro_id', ids.join(','))} />
        </LinhaChips>
      </section>

      <Tabela<Lancamento>
        tela="financeiro.lancamentos"
        caminho="/financeiro/lancamentos"
        // Lista de 30/10 (item 10): a busca global abre as Movimentações já buscando (`?busca=`).
        buscaNaUrl
        params={params}
        filtros={{
          campo_data: filtro('campo_data'),
          de: filtro('de'),
          ate: filtro('ate'),
          conta_id: filtro('conta_id'),
          categoria_id: filtro('categoria_id'),
          centro_custo_id: filtro('centro_custo_id'),
          tipo: filtro('tipo'),
          conciliado: filtro('conciliado'),
          origem: filtro('origem'),
          parceiro_id: filtro('parceiro_id'),
        }}
        aoAplicarFiltros={(f) => setBusca((atual) => filtrosNaUrl(atual, f), { replace: true })}
        colunas={colunas}
        entidade="financeiro.lancamento"
        aoClicar={(l) => setAberto(l.id)}
        vazio={
          <Vazio
            texto={filtrando ? t('vazio.lancamentosFiltro') : t('vazio.lancamentos')}
            acao={
              !filtrando && s.pode('financeiro.criar') ? (
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Botao icone={<Plus className="size-4" />} onClick={() => setNovo('entrada')}>
                    {t('acoes.novaEntrada')}
                  </Botao>
                  <Botao icone={<Minus className="size-4" />} onClick={() => setNovo('saida')}>
                    {t('acoes.novaSaida')}
                  </Botao>
                </div>
              ) : undefined
            }
          />
        }
      />

      <FormLancamento aberto={!!novo} tipo={novo ?? 'entrada'} aoFechar={() => setNovo(null)} />
      <FormTransferencia aberto={transferir} aoFechar={() => setTransferir(false)} />
      <ExportarContabil aberto={exportar} aoFechar={() => setExportar(false)} />
      <DetalheLancamento id={aberto} aoFechar={() => setAberto(null)} />
    </div>
  );
}
